import { useState } from "react";
import {
  DiscountTag02Icon,
  Link05Icon,
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon,
  UserAdd02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useQueryClient } from "@tanstack/react-query";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { Action } from "shared/components/ContextMenuPopover";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplicationActions } from "shared/utils/application-actions";
import { useDiscounts } from "views/discounts/hooks/useDiscounts";

import { Archive, ArrowRight, FlipBackward } from "untitledui-js-base";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";

export const usePopoverActions = ({
  applicationId,
  actions: appActions,
  templateId,
  onMoveClick,
  onRefundClick,
  onCancelClick,
  onUncancelClick,
  onSplitClick,
  onInviteApplicantClick,
  onShareWithOwnerClick,
  showShareWithOwner = false,
  activeOwnerLinks = 0
}: {
  applicationId: string | number;
  actions: ApplicationActions;
  templateId: string;
  onMoveClick?: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onRefundClick?: () => void;
  onSplitClick?: () => void;
  onInviteApplicantClick?: () => void;
  onShareWithOwnerClick?: () => void;
  // Only while Shareable applicant reports is switched on for the partner.
  showShareWithOwner?: boolean;
  // How many of this application's links are still live; zero shows no count.
  activeOwnerLinks?: number;
}) => {
  const { unit } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();

  const actions = appActions;

  const [showActiveDiscountCodes, setShowActiveDiscountCodes] = useState(false);
  const { data: availableDiscounts = [] } = useDiscounts({
    active: true,
    restriction_type: "template",
    template_id: templateId,
    enabled: showActiveDiscountCodes
  });

  const ret: Action[] = [];

  // Share report, first: it is the action a partner opens this menu for once
  // the screening is done, and the count tells them whether anyone already holds
  // a link without making them open the dialog.
  if (showShareWithOwner)
    ret.push({
      title: (
        <MenuRow
          text="Share report"
          HugeIcon={Link05Icon}
          badge={activeOwnerLinks}
        />
      ),
      onClick: () => onShareWithOwnerClick?.()
    });

  // Invite applicant (only while the decision, if any, can still be undone —
  // matches the previous standalone-button gating on undoDecision.hidden)
  const { inviteApplicant, undoDecision } = actions;
  if (undoDecision.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Invite applicant"
          HugeIcon={UserAdd02Icon}
          isDisabled={inviteApplicant.disabled}
        />
      ),
      disabled: inviteApplicant.disabled,
      tooltip: inviteApplicant.tooltip,
      onClick: () => onInviteApplicantClick?.()
    });

  // Move to another unit
  const { move } = actions;
  if (!move.hidden)
    ret.push({
      title: (
        <MenuRow
          text={`Move to another ${unit}`}
          Icon={ArrowRight}
          isDisabled={move.disabled}
        />
      ),
      tooltip: move.tooltip,
      disabled: move.disabled,
      onClick: () => onMoveClick?.()
    });

  // Split application
  const { split } = actions;
  if (!split.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Split application"
          HugeIcon={PathfinderDivideIcon}
          isDisabled={split.disabled}
        />
      ),
      disabled: split.disabled,
      tooltip: split.tooltip,
      onClick: () => onSplitClick?.()
    });

  // Resume / Place on hold
  const { resume, placeOnHold } = actions;

  if (!resume.hidden)
    ret.push({
      title: <MenuRow text="Resume" HugeIcon={PlayIcon} />,
      onClick: () => {
        resumeApplication({
          applicationId,
          onSuccess: () => {
            queryClient.refetchQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }
        });
      }
    });
  else if (!placeOnHold.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Place on hold"
          HugeIcon={PauseIcon}
          isDisabled={placeOnHold.disabled}
        />
      ),
      disabled: placeOnHold.disabled,
      tooltip: placeOnHold.tooltip,
      onClick: () => {
        placeOnHoldApplication({
          applicationId,
          onSuccess: () => {
            queryClient.refetchQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }
        });
      }
    });

  // Uncancel / Cancel
  const { uncancel, cancel } = actions;

  if (!uncancel.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Uncancel application"
          Icon={Archive}
          isDisabled={uncancel.disabled}
        />
      ),
      disabled: uncancel.disabled,
      tooltip: uncancel.tooltip,
      onClick: () => onUncancelClick?.()
    });
  else if (!cancel.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Cancel application"
          Icon={Archive}
          isDisabled={cancel.disabled}
        />
      ),
      tooltip: cancel.tooltip,
      disabled: cancel.disabled,
      onClick: () => onCancelClick?.()
    });

  // Refund application
  const { refund } = actions;
  if (!refund.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Refund application"
          Icon={FlipBackward}
          isDisabled={refund.disabled}
        />
      ),
      disabled: refund.disabled,
      tooltip: refund.tooltip,
      onClick: () => onRefundClick?.()
    });

  ret.push({
    title: <MenuRow text="View discount codes" HugeIcon={DiscountTag02Icon} />,
    onClick: () => setShowActiveDiscountCodes(true)
  });

  return {
    actions: ret,
    showActiveDiscountCodes,
    setShowActiveDiscountCodes,
    availableDiscounts
  };
};
